import { useMakeModelContext } from '../../../../../components/SearchPage/features/makeModel/MakeModelContext';
import { useMakeModelShared } from '../../../../../components/SearchPage/features/makeModel/hooks/MakeModelShared.hook';
import { useRouter } from 'next/router';
import { useFiltersState } from '../../../../../features/filters/Filters.context';
import { mapGetAdsRequestPayload } from '../../../../../features/filters/Filters.mapper';
import { shouldShowTrims } from '../MakeModel.constants';

const useMakeModelWriteState = (args: { section: string }) => {
  const { section } = args;

  const showTrims = shouldShowTrims(section);

  const { makeModels, setMakeModels } = useMakeModelContext();
  const { query } = useRouter();
  const { filtersData } = useFiltersState();

  const { getModelsWithCounts, getTrimsWithCount } = useMakeModelShared({
    section,
  });

  const selectMake = (makeValue: string) => {
    if (!makeValue) {
      selectDefaultMakeOption();
      return;
    }
    if (showTrims) {
      const editableMake = makeModels?.find(
        (item) => item.make === makeValue && item.isEditable,
      );
      const nonEditableMakeWithoutModel = makeModels?.find(
        (item) =>
          item.make === makeValue &&
          !item.isEditable &&
          (!item.model || item.model.length === 0 || item.model[0] === ''),
      );

      if (editableMake) {
        // Clicking a make that's already editable should deselect it
        const filteredMakeModels = makeModels?.filter(
          (item) => !(item.make === makeValue && item.isEditable),
        );
        setMakeModels(
          filteredMakeModels && filteredMakeModels.length > 0
            ? filteredMakeModels
            : undefined,
        );
      } else if (nonEditableMakeWithoutModel) {
        // Non-editable make without specific model exists - clear it
        const filteredMakeModels = makeModels?.filter(
          (item) => item.make !== makeValue,
        );
        setMakeModels(
          filteredMakeModels && filteredMakeModels.length > 0
            ? filteredMakeModels
            : undefined,
        );
      } else {
        // No editable make exists, and any non-editable ones have models - add new editable make
        const filteredMakeModels = makeModels?.filter(
          (item) => item.isEditable !== true,
        );

        const payload = mapGetAdsRequestPayload({ query, filtersData });

        getModelsWithCounts(makeValue, payload);

        setMakeModels([
          ...(filteredMakeModels ?? []),
          { make: makeValue, isEditable: true },
        ]);
      }
    } else {
      const includesMake = makeModels?.some((item) => item.make === makeValue);
      if (includesMake) {
        clearMake(makeValue);
      } else if (!makeValue) {
        const filteredMakeModels = makeModels?.filter(
          (item) => item.isEditable !== true,
        );
        setMakeModels([...(filteredMakeModels ?? [])]);
      } else {
        const filteredMakeModels = makeModels?.filter(
          (item) => item.isEditable !== true,
        );

        const payload = mapGetAdsRequestPayload({ query, filtersData });

        getModelsWithCounts(makeValue, payload);

        setMakeModels([
          ...(filteredMakeModels ?? []),
          { make: makeValue, isEditable: true },
        ]);
      }
    }
  };

  const selectDefaultMakeOption = () => {
    // Remove the make associated with the editable group of make models, if any, based on whether an editable make group exists
    if (makeModels) {
      const editableMake = makeModels.find(
        ({ isEditable }) => isEditable,
      )?.make;
      if (editableMake) {
        // Only clear the editable make, not all makes with this value
        const filteredMakeModels = makeModels.filter(
          (item) => !item.isEditable,
        );
        setMakeModels(
          filteredMakeModels && filteredMakeModels.length > 0
            ? filteredMakeModels
            : undefined,
        );
      }
    }
  };

  const selectModel = (makeValue: string, option: string) => {
    // Only filter out the editable make-model combination, not all makes with the same name
    const filteredMakes = makeModels?.filter(
      (item) => !(item.make === makeValue && item.isEditable),
    );

    if (!option) {
      setMakeModels([
        ...(filteredMakes ?? []),
        {
          make: makeValue,
          isEditable: true,
        },
      ]);
    } else {
      // Get models from the editable make-model combination only
      const selectedModels = makeModels
        ?.find((item) =>
          showTrims
            ? item.make === makeValue && item.isEditable
            : item.make === makeValue,
        )
        ?.model?.filter((item) => item);

      const modelValue = showTrims
        ? selectedModels?.includes(option)
          ? [] // Deselect if clicking same model
          : [option] // Single selection when showTrims is enabled
        : selectedModels?.find((item) => item === option)
        ? selectedModels.filter((item) => item !== option)
        : [...(selectedModels ?? []), option]; // Multi-select toggle when showTrims is disabled

      if (showTrims) {
        const payload = mapGetAdsRequestPayload({ query, filtersData });

        getTrimsWithCount(makeValue, modelValue[0], payload);
      }

      setMakeModels([
        ...(filteredMakes ?? []),
        {
          make: makeValue,
          model: modelValue,
          isEditable: true,
        },
      ]);
    }
  };

  const selectTrim = (
    makeValue: string,
    _modelValue: string,
    option: string,
  ) => {
    // Only filter out the editable make-model combination, not all makes with the same name
    const filteredMakes = makeModels?.filter(
      (item) => !(item.make === makeValue && item.isEditable),
    );

    if (!option) {
      // Clear trims - keep make and model but remove trims
      const currentMakeModel = makeModels?.find((item) =>
        showTrims
          ? item.make === makeValue && item.isEditable
          : item.make === makeValue,
      );
      setMakeModels([
        ...(filteredMakes ?? []),
        {
          make: makeValue,
          model: currentMakeModel?.model,
          isEditable: true,
        },
      ]);
    } else {
      // Get trims from the editable make-model combination only
      const selectedTrims = makeModels
        ?.find((item) =>
          showTrims
            ? item.make === makeValue && item.isEditable
            : item.make === makeValue,
        )
        ?.trim?.filter((item) => item);

      const trimValue = selectedTrims?.find((item) => item === option)
        ? selectedTrims.filter((item) => item !== option)
        : [...(selectedTrims ?? []), option];

      const currentMakeModel = makeModels?.find((item) =>
        showTrims
          ? item.make === makeValue && item.isEditable
          : item.make === makeValue,
      );

      const updatedMakeModels = [
        ...(filteredMakes ?? []),
        {
          make: makeValue,
          model: currentMakeModel?.model,
          trim: trimValue,
          isEditable: true,
        },
      ];

      setMakeModels(updatedMakeModels);
    }
  };

  const clearMake = (makeValue: string) => {
    const filteredMakes = makeModels?.filter(({ make }) => make !== makeValue);

    setMakeModels(
      filteredMakes && filteredMakes.length > 0 ? filteredMakes : undefined,
    );
  };

  const onAddSelection = () => {
    const mappedMakeModels = makeModels?.map((item) => {
      return {
        ...item,
        isEditable: false,
      };
    });
    setMakeModels(mappedMakeModels);
  };

  const isStandaloneMake = (item: { model?: string[] }) =>
    !item.model ||
    item.model.length === 0 ||
    (item.model.length === 1 && item.model[0] === '');

  const findMakeModelCombination = (
    make: string,
    model?: string,
    trim?: string,
  ) => {
    return (
      makeModels?.findIndex((item) => {
        if (!model) {
          // Removing standalone make: match make with no specific models
          return item.make === make && isStandaloneMake(item);
        }

        const isMakeMatch = item.make === make;
        const isModelMatch = item.model?.includes(model);
        const isTrimMatch = showTrims
          ? !trim || item.trim?.includes(trim)
          : true;

        return isMakeMatch && isModelMatch && isTrimMatch;
      }) ?? -1
    );
  };

  const onClearSelection = (make: string, model?: string, trim?: string) => {
    if (!makeModels) return;

    const makeModelTrimSelectionIndex = findMakeModelCombination(
      make,
      model,
      trim,
    );
    if (makeModelTrimSelectionIndex === -1) return;

    const updatedMakeModels = makeModels.filter(
      (_, index) => index !== makeModelTrimSelectionIndex,
    );
    setMakeModels(updatedMakeModels.length > 0 ? updatedMakeModels : undefined);
  };

  return {
    selectMake,
    selectModel,
    selectTrim,
    onAddSelection,
    onClearSelection,
  };
};

export { useMakeModelWriteState };
